import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useLocalStore } from "../Hooks/useLocalStore";
import SimpleAccordion from "./PrimaryAccordion/SimpleAccordion";
import "./RezultataiTable/RezultataiTable.css"; // Import the CSS file
import {
  useHandleCapture,
  useRezultataiProcessor,
  usePlotLanguage,
  useScrollToQuestion,
} from "./RezultataiTable/hooks";
import useContainerElement from "./RezultataiTable/useContainerElement";

import { VirtualizedQuestionList } from "./RezultataiTable/components";

const RezultataiTable = memo(({ tipai, atskleistas, info, filtrai, metai, handleOpen }) => {
  const { t } = useTranslation();
  const { data: klausimas, setData: setKlausimas } = useLocalStore("klausimas");
  const { setData: setTipas } = useLocalStore("tipas");
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set()); // Initialize with the provided question ID or an empty set
  const [expandedPanel, setExpandedPanel] = useState(atskleistas > -1 ? atskleistas : undefined); // State for the currently expanded accordion panel index
  const containerElement = useContainerElement(".RezultataiTable");

  const plotLanguage = usePlotLanguage();
  const { atsakymai, puslapiai, visiAts, error } = useRezultataiProcessor(
    info,
    tipai,
    filtrai
  );
  // FIXED: Moved all hook calls to the top level, before conditional returns
  const handleCapture = useHandleCapture();

  // Also moved this hook to the top level to follow React's rules of hooks
  useScrollToQuestion(
    klausimas,
    { current: containerElement },
    setKlausimas,
    setTipas,
    activeQuestionId,
    setActiveQuestionId
  );

  if (error) {
    return (
      <div className="RezultataiTable">
        <div className="error-container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  // Optimizuota versija - pridedamas activeQuestionId, kad išvengti nereikalingų scrollų
  // FIXED: Moved this hook call to the top of the component

  // Accordion išskleidimo funkcija - optimizuota multi-select palaikymui
  // Updated handler to manage the expanded panel state locally
  const handleExpandChange = (pageIndex) => {
    const newExpandedPanel = expandedPanel === pageIndex ? null : pageIndex;
    setExpandedPanel(newExpandedPanel);
    // Call the original handleOpen (passed from parent) if needed,
    // though its original purpose might be obsolete now.
    if (handleOpen) {
      handleOpen(newExpandedPanel !== null ? pageIndex : -1); // Pass index or -1
    }
  };

  const handleQuestionExpand = (questionId, isExpanded) => {
    setExpandedQuestions((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(questionId);
      } else {
        newSet.delete(questionId);
      }
      return newSet;
    });
  };

  return (
    <div
      className="RezultataiTable"
      style={{ display: "flex", flexDirection: "column" }}
    >
      {Object.entries(puslapiai).map(([pageName, pageElements], pageIndex) => (
        <div
          key={`${pageName}-${pageIndex}-table`}
          className={`qeastionPage ${pageName}`}
          style={{ width: "100%", marginBottom: "1rem" }}
        >
          <div
            className="pageContent borderBot"
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <SimpleAccordion
              id={`page-${pageIndex}`}
              title={
                <div className="pageName">
                  <div className="pageTitle">{t(pageName)}</div>
                </div>
              }
              className="rezAccordion"
              headerClassName="rezAccSumm"
              contentClassName="rezAccSummVid"
              expandIconClassName="expandIconRez"
              // Control expansion state from parent
              expanded={expandedPanel === pageIndex}
              // Pass only the index to the handler
              onToggle={() => handleExpandChange(pageIndex)}
            >
              {pageElements && (
                <VirtualizedQuestionList
                  elements={Object.values(pageElements).map((element) => ({
                    ...element,
                    pageName,
                  }))}
                  plotLanguage={plotLanguage}
                  klausimas={klausimas}
                  visiAts={visiAts}
                  atsakymai={atsakymai}
                  tipai={tipai}
                  metai={metai}
                  handleCapture={handleCapture}
                  questionRef={{ current: containerElement }}
                  activeQuestionId={activeQuestionId}
                  setActiveQuestionId={setActiveQuestionId}
                  expandedQuestions={expandedQuestions}
                  onQuestionExpand={handleQuestionExpand}
                  windowSize={20} // Show 20 items at a time
                />
              )}
            </SimpleAccordion>
          </div>
        </div>
      ))}
    </div>
  );
});

RezultataiTable.propTypes = {
  tipai: PropTypes.array.isRequired,
  info: PropTypes.array.isRequired,
  filtrai: PropTypes.object,
  metai: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null, undefined]), // Allow null/undefined during form switching
  ]),
  handleOpen: PropTypes.func,
};

RezultataiTable.defaultProps = {
  filtrai: {},
  metai: null,
  handleOpen: () => {},
};

RezultataiTable.displayName = "RezultataiTable";

export default RezultataiTable;
